"use client"
import React from "react"
import Link from "next/link"
import { useSearchParams, useRouter } from "next/navigation"
import { cn } from "@lib/util"

interface ClientLinkProps
  extends Omit<React.ComponentProps<typeof Link>, "href"> {
  href: string
  className?: string
  children: React.ReactNode
}

const ClientLink: React.FC<ClientLinkProps> = ({
  href,
  children,
  className,
  onClick,
  ...props
}) => {
  const searchParams = useSearchParams()
  const router = useRouter()

  const hideHeader = searchParams.get("hideHeader")
  const hideFooter = searchParams.get("hideFooter")

  const normalizeUrl = (url: URL): string => {
    let path = url.pathname
    if (path.startsWith("/en/")) {
      path = path.slice(3) // 移除开头的 '/en'
    } else if (path === "/en") {
      path = "/"
    }
    return path + url.search
  }

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    onClick?.(e)
    if (e.defaultPrevented) {
      return
    }

    e.preventDefault()

    const newUrl = new URL(href, window.location.origin)

    if (hideHeader) newUrl.searchParams.set("hideHeader", hideHeader)
    if (hideFooter) newUrl.searchParams.set("hideFooter", hideFooter)

    const newHref = newUrl.pathname + newUrl.search

    const currentUrl = new URL(window.location.href)
    const normalizedCurrentUrl = normalizeUrl(currentUrl)
    const normalizedNewUrl = normalizeUrl(newUrl)

    if (normalizedCurrentUrl !== normalizedNewUrl) {
      e.stopPropagation()
      router.push(newHref)
      return
    }

    e.stopPropagation()
    window.history.pushState(null, "", window.location.href)
  }

  return (
    <Link
      className={cn(className)}
      href={href}
      onClick={handleClick}
      {...props}
    >
      {children}
    </Link>
  )
}

export default ClientLink
